import React, { useState } from 'react';
import { clientOnly } from './clientOnly';
import type { ReactPlayerProps } from 'react-player';
import { useNearViewport } from '../../hooks/useNearViewport';

const ReactPlayer = clientOnly(() => import('react-player'));

const toCss = (value: ReactPlayerProps['width']) =>
    typeof value === 'number' ? `${value}px` : value;

/**
 * react-player, loaded only in the browser when it is needed. Until then a
 * placeholder with the player's wrapper size (and, for `light` previews, the
 * same thumbnail and play icon) holds its place:
 * - `light` thumbnails load the player on click.
 * - Everything else loads when it comes near the viewport.
 */
const LazyReactPlayer: React.FC<ReactPlayerProps> = (props) => {
    const {
        width = '640px',
        height = '360px',
        style,
        className,
        light,
        playIcon,
        onClickPreview,
    } = props;
    const hasThumbnail = typeof light === 'string' && light !== '';
    const [clicked, setClicked] = useState(false);
    const [ref, isNear] = useNearViewport<HTMLDivElement>(!hasThumbnail);

    const shouldLoad = hasThumbnail ? clicked : isNear;
    const wrapperStyle = {
        ...style,
        width: toCss(width),
        height: toCss(height),
    };

    if (shouldLoad) {
        // After a thumbnail click react-player shows the active player, not
        // its preview, so skip straight to that state.
        return <ReactPlayer {...props} light={hasThumbnail ? false : light} />;
    }

    if (!hasThumbnail) {
        return <div ref={ref} className={className} style={wrapperStyle} />;
    }

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        setClicked(true);
        onClickPreview?.(e);
    };

    return (
        <div className={className} style={wrapperStyle}>
            <div
                className="react-player__preview"
                role="button"
                tabIndex={0}
                aria-label="Play video"
                onClick={handleClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleClick(e);
                }}
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${light})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {playIcon}
            </div>
        </div>
    );
};

export default LazyReactPlayer;
